import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import * as moment from 'moment';
import { ConfigService } from 'src/app/service/config.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import {
  ScoreRequestTypes,
  ScoreTimeSeriesResult,
  ScoreTimeSeriesValue,
} from 'src/app/shared/models/score-time-series.interface';
import { QueryParams } from 'src/app/shared/models/query-params.interface';

export interface PortfolioChartEntity {
  name: string;
  compoundKey: string;
  series: ScoreTimeSeriesValue[];
}

export interface PortfolioChartModel {
  isSentiment: boolean;
  /** Active portfolio name — the label for the aggregate line. */
  portfolioName: string;
  /** Aggregate "Portfolio" line for the selected window (collapsed across members). */
  portfolioSeries: ScoreTimeSeriesValue[];
  /** Aggregate over the full available range — the navigator scrubber's data. */
  navigatorSeries: ScoreTimeSeriesValue[];
  /** Per-entity series for the selected window, ordered to match portfolio content. */
  entities: PortfolioChartEntity[];
}

type EntityNameMap = Record<string, { name?: string | null }>;

const EMPTY_MODEL: PortfolioChartModel = {
  isSentiment: false,
  portfolioName: 'Portfolio',
  portfolioSeries: [],
  navigatorSeries: [],
  entities: [],
};

// The per-entity call carries every portfolio member as an event_participant.
// score_ts declares no maxItems, but the legacy chart chunked the itemized call
// at 100 and the entity-count ceiling is still unverified — keep chunking.
const CHUNK_SIZE = 100;

/**
 * Fetches the portfolio benchmark chart data via the v2 `score_ts` endpoint.
 *
 * Every score type goes through the same request — only the resolved `score_type`
 * (sentiment → `{ period }`, everything else → `resolveScoreType`) differs. Reads:
 *  - aggregate over the selected window  → the "Portfolio" line
 *  - aggregate over the full data range  → the navigator scrubber (matches legacy `izoom`)
 *  - per-entity over the selected window → one line per member
 *  - entity_info (cached)                → legend labels
 */
@Injectable({
  providedIn: 'root',
})
export class PortfolioChartDataService {
  // Navigator (wide) aggregate cache — see navigatorSeries().
  private navigatorKey: string | null = null;
  private navigator$: Observable<ScoreTimeSeriesValue[]> | null = null;

  constructor(
    private scoreTimeSeriesService: ScoreTimeSeriesService,
    private scoreUtils: ScoreUtilsService,
    private featureFlags: FeatureFlagsService,
    private portfolioApi: PortfolioApiService,
    private entityInfoService: EntityInfoService,
    private config: ConfigService,
  ) {}

  fetchPortfolioChartData(
    params: QueryParams,
  ): Observable<PortfolioChartModel> {
    const content: string[] = this.portfolioApi.activePortfolioContent;
    const defaultScore = this.featureFlags.scorelabSettings.defaultScore;
    const isSentiment = defaultScore === 'sentiment';
    const portfolioName =
      this.portfolioApi.activePortfolio?.name || 'Portfolio';

    if (!content || content.length === 0) {
      return of({ ...EMPTY_MODEL, isSentiment, portfolioName });
    }

    const scoreType: ScoreRequestTypes = isSentiment
      ? { period: this.resolvePeriod(params) }
      : this.scoreUtils.resolveScoreType(defaultScore);

    return forkJoin({
      aggregateWeek: this.scoreTimeSeriesService.fetchCollapsedSeries(
        scoreType,
        {
          entities: content,
          time: params.time,
        },
      ),
      aggregateWide: this.navigatorSeries(scoreType, content, params),
      perEntity: this.fetchPerEntity(scoreType, content, params.time),
      names: this.entityInfoService.getEntitiesInfo(content, 'object').pipe(
        map((result) => result as EntityNameMap),
        catchError(() => of({} as EntityNameMap)),
      ),
    }).pipe(
      map(({ aggregateWeek, aggregateWide, perEntity, names }) =>
        this.buildModel(
          isSentiment,
          portfolioName,
          content,
          aggregateWeek,
          aggregateWide,
          perEntity,
          names,
        ),
      ),
      catchError(() => of({ ...EMPTY_MODEL, isSentiment, portfolioName })),
    );
  }

  /**
   * Navigator scrubber data: the collapsed aggregate over the full data range.
   * Cached per portfolio + score (+ `update` force-refresh) so dragging the
   * navigator (which changes only `time`) doesn't re-issue this heavy request.
   */
  private navigatorSeries(
    scoreType: ScoreRequestTypes,
    content: string[],
    params: QueryParams,
  ): Observable<ScoreTimeSeriesValue[]> {
    const key = JSON.stringify({
      content,
      scoreType,
      update: params.update ?? null,
    });
    if (this.navigatorKey !== key || !this.navigator$) {
      this.navigatorKey = key;
      this.navigator$ = this.scoreTimeSeriesService
        .fetchCollapsedSeries(scoreType, {
          entities: content,
          time: this.fullRange(),
        })
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.navigator$;
  }

  private fetchPerEntity(
    scoreType: ScoreRequestTypes,
    content: string[],
    time: string | undefined,
  ): Observable<ScoreTimeSeriesResult[]> {
    const chunks: string[][] = [];
    for (let i = 0; i < content.length; i += CHUNK_SIZE) {
      chunks.push(content.slice(i, i + CHUNK_SIZE));
    }

    return forkJoin(
      chunks.map((chunk) =>
        this.scoreTimeSeriesService.fetchEntityResults(scoreType, {
          entities: chunk,
          time,
        }),
      ),
    ).pipe(map((results) => results.flat()));
  }

  private buildModel(
    isSentiment: boolean,
    portfolioName: string,
    content: string[],
    aggregateWeek: ScoreTimeSeriesValue[],
    aggregateWide: ScoreTimeSeriesValue[],
    perEntityResults: ScoreTimeSeriesResult[],
    names: EntityNameMap,
  ): PortfolioChartModel {
    const byKey = new Map<string, ScoreTimeSeriesResult>();
    perEntityResults.forEach((result) => {
      const key = result.entity_info?.compound_key;
      if (key) {
        byKey.set(key, result);
      }
    });

    const entities: PortfolioChartEntity[] = content.map((compoundKey) => ({
      name:
        names[compoundKey]?.name ||
        byKey.get(compoundKey)?.entity_info?.name ||
        compoundKey,
      compoundKey,
      series: byKey.get(compoundKey)?.score_ts ?? [],
    }));

    return {
      isSentiment,
      portfolioName,
      portfolioSeries: aggregateWeek,
      navigatorSeries: aggregateWide,
      entities,
    };
  }

  /** Full available data range (`startDate--today`) — same window the legacy `izoom` used. */
  private fullRange(): string {
    const start = this.config.appConfig?.setup?.startDate;
    const end = moment().format('YYYY-MM-DD');
    return `${start}--${end}`;
  }

  private resolvePeriod(params: QueryParams): number {
    const period = params.period ? parseInt(params.period as string, 10) : 7;
    return Number.isFinite(period) && period > 0 ? period : 7;
  }
}
